import { n as snippet, r as isSymbolicOperator } from "./dvala.js";
import { datatype } from "../datatype.js";
import { allStandardEffectDefinitions, arrayNormalExpression, assertModule, assertionNormalExpression, astModule, bitwiseNormalExpression, bitwiseUtilsModule, collectionNormalExpression, collectionUtilsModule, convertModule, functionalNormalExpression, functionalUtilsModule, getMetaNormalExpression, gridModule, handlerModule, isCustomReference, isDatatypeReference, isEffectReference, isFunctionDocs, isFunctionReference, isFunctionReference as isFunctionReference$1, isPreludeReference, isShorthandReference, jsonModule, linearAlgebraModule, macrosModule, mathNormalExpression, mathUtilsModule, matrixModule, miscNormalExpression, normalExpressions, numberTheoryModule, objectNormalExpression, predicatesNormalExpression, regexpNormalExpression, sequenceNormalExpression, sequenceUtilsModule, specialExpressions, stringNormalExpression, stringUtilsModule, timeModule, vectorModule } from "@mojir/dvala-engine";
import { canBeOperator, specialExpressionTypes } from "@mojir/dvala-types";
//#region src/reference/prelude.ts
/**
* Reference entries for the standard prelude type aliases declared in
* `src/prelude.dvala`. Keep `definition` in sync with the alias body
* there — these are documentation surfaces and the prelude file is the
* source of truth for typechecker semantics.
*/
const prelude = {
	"-prelude-Positive": {
		prelude: true,
		title: "Positive",
		category: "prelude",
		definition: "Number & {n | n > 0}",
		description: "Strictly positive numbers (n > 0). Use as a precondition where zero and negatives are both invalid — e.g. divisor in a count-style formula, quantities, lengths.",
		examples: [snippet("let x: Positive = 5"), snippet("let f = (n: Positive) -> 100 / n")],
		seeAlso: ["-prelude-NonNegative", "-prelude-NonZero"]
	},
	"-prelude-NonNegative": {
		prelude: true,
		title: "NonNegative",
		category: "prelude",
		definition: "Number & {n | n >= 0}",
		description: "Non-negative numbers (n >= 0). The natural-number sense from CS where 0 is included; common precondition for array indices and counts.",
		examples: [snippet("let i: NonNegative = 0"), snippet("let f = (idx: NonNegative) -> idx + 1")],
		seeAlso: ["-prelude-Positive", "-prelude-NonZero"]
	},
	"-prelude-NonZero": {
		prelude: true,
		title: "NonZero",
		category: "prelude",
		definition: "Number & {n | n != 0}",
		description: "Non-zero numbers (n != 0). The minimal precondition for division — `Positive` is stricter than needed when negative divisors are also fine.",
		examples: [snippet("let d: NonZero = -3"), snippet("let safeDivide = (a: Number, b: NonZero) -> a / b")],
		seeAlso: ["-prelude-Positive", "-prelude-NonNegative"]
	},
	"-prelude-NonEmpty": {
		prelude: true,
		title: "NonEmpty",
		category: "prelude",
		definition: "T & {xs | count(xs) > 0} where T: Sequence",
		description: "Non-empty sequences (count(xs) > 0). Generic over the `Sequence` upper bound, so it works for both arrays and strings — `NonEmpty<Array<Number>>` and `NonEmpty<String>` are both valid.",
		examples: [snippet("let xs: NonEmpty<Number[]> = [1, 2, 3]"), snippet("let s: NonEmpty<String> = \"hi\"")]
	}
};
//#endregion
//#region src/reference/shorthand.ts
const shorthand = {
	"-short-regexp": {
		shorthand: true,
		title: "#\"pattern\"",
		category: "shorthand",
		description: "Shorthand for `regexp(pattern)`. Only difference is that escaping is not needed.",
		examples: [snippet("#\"^\\s*(.*)$\""), snippet("#\"albert\"ig")],
		seeAlso: [
			"regexp",
			"reMatch",
			"replace",
			"replaceAll"
		]
	},
	"-short-fn": {
		shorthand: true,
		title: "-> expression",
		category: "shorthand",
		description: `
Shorthand for \`(args, ...) -> expression\`.
\`$\` is the first argument, and \`$2, $3, ...\` are the second, third, ... argument.`,
		examples: [snippet("-> $ + $2"), snippet("(-> $ * $)(9)")]
	},
	"-short-object": {
		shorthand: true,
		title: "{ foo }",
		category: "shorthand",
		description: "Shorthand for `{ foo: foo }`. When a key and variable name are the same, you can omit the value.",
		examples: [snippet("let x = 1; let y = 2; { x, y }"), snippet("let name = \"Alice\"; let age = 30; { name, age }")]
	}
};
//#endregion
//#region src/reference/index.ts
function docsToReference(expressions) {
	const result = {};
	for (const [key, expr] of Object.entries(expressions)) {
		const docs = expr.docs;
		if (!docs) throw new Error(`Missing docs for expression "${key}"`);
		result[key] = {
			title: key,
			category: docs.category,
			description: docs.description,
			returns: docs.returns,
			args: docs.args,
			variants: docs.variants,
			examples: docs.examples,
			...docs.seeAlso ? { seeAlso: docs.seeAlso } : {},
			...docs.hideOperatorForm ? { noOperatorDocumentation: true } : {}
		};
	}
	return result;
}
function moduledDocsToReference(module) {
	const result = {};
	for (const [key, docs] of Object.entries(module.docs ?? {})) {
		const qualifiedKey = `${module.name}.${key}`;
		result[qualifiedKey] = {
			title: qualifiedKey,
			category: docs.category,
			description: docs.description,
			returns: docs.returns,
			args: docs.args,
			variants: docs.variants,
			examples: docs.examples,
			...docs.seeAlso ? { seeAlso: docs.seeAlso } : {},
			...docs.hideOperatorForm ? { noOperatorDocumentation: true } : {}
		};
	}
	return result;
}
const assertionRef = docsToReference(assertionNormalExpression);
const bitwiseReference = docsToReference(bitwiseNormalExpression);
const arrayRef = docsToReference(arrayNormalExpression);
const collectionRef = docsToReference(collectionNormalExpression);
const functionalRef = docsToReference(functionalNormalExpression);
const mathRef = docsToReference(mathNormalExpression);
const metaRef = docsToReference(getMetaNormalExpression({}, {}));
const miscRef = docsToReference(miscNormalExpression);
const objectRef = docsToReference(objectNormalExpression);
const predicatesRef = docsToReference(predicatesNormalExpression);
const regexpRef = docsToReference(regexpNormalExpression);
const sequenceRef = docsToReference(sequenceNormalExpression);
const stringRef = docsToReference(stringNormalExpression);
function specialExpressionDocsToReference() {
	const result = {};
	for (const [name, type] of Object.entries(specialExpressionTypes)) {
		const docs = specialExpressions[type]?.docs;
		if (!docs) continue;
		if (isFunctionDocs(docs)) result[name] = {
			title: name,
			category: docs.category,
			description: docs.description,
			returns: docs.returns,
			args: docs.args,
			variants: docs.variants,
			examples: docs.examples,
			...docs.seeAlso ? { seeAlso: docs.seeAlso } : {},
			...docs.hideOperatorForm ? { noOperatorDocumentation: true } : {}
		};
		else result[name] = {
			title: name,
			category: docs.category,
			description: docs.description,
			customVariants: docs.customVariants,
			...docs.details ? { details: docs.details } : {},
			...docs.returns ? { returns: docs.returns } : {},
			examples: docs.examples,
			...docs.seeAlso ? { seeAlso: docs.seeAlso } : {}
		};
	}
	return result;
}
const specialExpressionsReference = specialExpressionDocsToReference();
const normalExpressionReference = {
	...assertionRef,
	...bitwiseReference,
	...collectionRef,
	...arrayRef,
	...sequenceRef,
	...mathRef,
	...functionalRef,
	...metaRef,
	...miscRef,
	...objectRef,
	...predicatesRef,
	...regexpRef,
	...stringRef
};
const moduleReference = {
	...moduledDocsToReference(assertModule),
	...moduledDocsToReference(gridModule),
	...moduledDocsToReference(vectorModule),
	...moduledDocsToReference(linearAlgebraModule),
	...moduledDocsToReference(matrixModule),
	...moduledDocsToReference(numberTheoryModule),
	...moduledDocsToReference(stringUtilsModule),
	...moduledDocsToReference(collectionUtilsModule),
	...moduledDocsToReference(sequenceUtilsModule),
	...moduledDocsToReference(mathUtilsModule),
	...moduledDocsToReference(functionalUtilsModule),
	...moduledDocsToReference(bitwiseUtilsModule),
	...moduledDocsToReference(convertModule),
	...moduledDocsToReference(jsonModule),
	...moduledDocsToReference(timeModule),
	...moduledDocsToReference(handlerModule),
	...moduledDocsToReference(astModule),
	...moduledDocsToReference(macrosModule)
};
Object.entries(normalExpressionReference).forEach(([key, obj]) => {
	if (!normalExpressions[key]) throw new Error(`Missing normal expression ${key} in normalExpressions`);
	const arity = normalExpressions[key].arity;
	if (!obj.noOperatorDocumentation && canBeOperator(arity)) {
		obj._isOperator = true;
		if (isSymbolicOperator(key)) obj._prefereOperator = true;
	}
});
Object.entries(specialExpressionsReference).forEach(([key, obj]) => {
	if (isFunctionReference(obj)) {
		const arity = specialExpressions[specialExpressionTypes[key]]?.arity;
		if (arity && canBeOperator(arity)) obj._isOperator = true;
	}
});
const apiReference = sortByCategory({
	...normalExpressionReference,
	...specialExpressionsReference,
	...shorthand,
	...datatype,
	...prelude
});
function deriveEffectReference() {
	const result = {};
	for (const [name, def] of Object.entries(allStandardEffectDefinitions)) {
		const key = `-effect-${name}`;
		result[key] = {
			effect: true,
			title: name,
			category: "effect",
			description: def.docs.description,
			args: def.docs.args,
			returns: def.docs.returns,
			variants: def.docs.variants,
			examples: def.docs.examples,
			...def.docs.seeAlso ? { seeAlso: def.docs.seeAlso } : {}
		};
	}
	return result;
}
const effectReference = deriveEffectReference();
const allReference = sortByCategory({
	...apiReference,
	...moduleReference,
	...effectReference
});
function sortByCategory(ref) {
	return Object.fromEntries(Object.entries(ref).sort(([keyA, refA], [keyB, refB]) => {
		const catA = refA.category === "special-expression" ? "" : refA.category;
		const catB = refB.category === "special-expression" ? "" : refB.category;
		if (catA !== catB) return catA.localeCompare(catB);
		return keyA.localeCompare(keyB);
	}));
}
/** Build a URL-safe linkName. Replaces %2F (slash) with ~ to avoid path-separator issues. */
function makeLinkName(category, key) {
	return encodeURIComponent(`${category}-${key}`).replace(/%2F/gi, "~");
}
function getLinkName(reference) {
	return makeLinkName(reference.category, reference.title);
}
//#endregion
export { isCustomReference as a, isFunctionReference$1 as c, makeLinkName as d, moduleReference as f, getLinkName as i, isPreludeReference as l, apiReference as n, isDatatypeReference as o, normalExpressionReference as p, effectReference as r, isEffectReference as s, allReference as t, isShorthandReference as u };

//# sourceMappingURL=reference.js.map